import type {
  LimitedText,
  ServerNotification,
  ServerResponseData,
  ServerResponseMode,
  ServerResponseTab,
  ServerResponseView,
} from '../../core/interfaces';
import { translateNow } from '../../core/services/i18n/translate-now';
import { toSortedJson } from './json-view.util';

/** Most UTF-16 characters of formatted text the response viewer renders; the rest is cut. */
export const SERVER_RESPONSE_DISPLAY_LIMIT = 1_048_576;

/** Keys of a Fury error's `details` that may hold the original message, in priority order. */
const DETAIL_MESSAGE_KEYS = ['message', 'error_description', 'originalMessage', 'error'] as const;

/** Keys of an unknown body that may hold a message, in priority order. */
const PLAIN_MESSAGE_KEYS = ['message', 'error', 'detail'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/** Whether a captured body or headers object has nothing to show. */
function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return isRecord(value) && Object.keys(value).length === 0;
}

function extractDetailMessage(details: unknown): string | undefined {
  if (!isRecord(details)) return nonEmptyString(details);
  const auth0 = details['auth0Response'];
  const fromAuth0 = isRecord(auth0) ? nonEmptyString(auth0['message']) : undefined;
  if (fromAuth0) return fromAuth0;
  for (const key of DETAIL_MESSAGE_KEYS) {
    const found = nonEmptyString(details[key]);
    if (found) return found;
  }
  return undefined;
}

/**
 * Decides how a response body should be presented: Fury's envelope, Fury's error, a plain
 * message, or raw JSON. Server text is returned verbatim, never translated.
 *
 * @param body Response body as captured.
 * @returns The first presentation that applies and the parts it shows.
 * @example
 * classifyServerResponse({ processId: 'p-1', message: 'Done', operationPayload: [] }).kind; // 'envelope'
 * classifyServerResponse('Gateway timeout').kind; // 'message'
 */
export function classifyServerResponse(body: unknown): ServerResponseView {
  if (isEmptyValue(body)) return { kind: 'empty' };

  if (isRecord(body)) {
    const message = nonEmptyString(body['message']);
    const processId = nonEmptyString(body['processId']);
    if (message && processId) {
      return { kind: 'envelope', message, processId, payload: body['operationPayload'] };
    }
    const errorId = nonEmptyString(body['errorId']);
    if (message && (errorId || body['category'] !== undefined || body['details'] !== undefined)) {
      return {
        kind: 'error',
        message,
        errorId,
        details: body['details'],
        detailMessage: extractDetailMessage(body['details']),
      };
    }
    for (const key of PLAIN_MESSAGE_KEYS) {
      const found = nonEmptyString(body[key]);
      if (found) return { kind: 'message', message: found };
    }
    return { kind: 'json' };
  }

  const text = nonEmptyString(body);
  return text ? { kind: 'message', message: text } : { kind: 'json' };
}

/**
 * Cuts a text to a display limit at the last complete line, so a huge response never
 * freezes the page.
 *
 * @param text Full formatted text.
 * @param maxChars Limit in UTF-16 characters.
 * @example
 * limitForDisplay('aaaa\nbbbb\ncccc', 11).text; // 'aaaa\nbbbb'
 */
export function limitForDisplay(
  text: string,
  maxChars: number = SERVER_RESPONSE_DISPLAY_LIMIT,
): LimitedText {
  const totalChars = text.length;
  if (totalChars <= maxChars) return { text, truncated: false, totalChars };
  const head = text.slice(0, maxChars + 1);
  const lastNewline = head.lastIndexOf('\n');
  const cut = lastNewline > 0 ? head.slice(0, lastNewline) : text.slice(0, maxChars);
  return { text: cut, truncated: true, totalChars };
}

/**
 * Formats a length for the truncation notice: whole KB (rounded up) below 1 MiB, otherwise
 * MB with one decimal and a comma. "MB" means 1,048,576 characters.
 *
 * @example
 * formatSize(3_355_443); // '3,2 MB'
 */
export function formatSize(chars: number): string {
  if (chars < 1_048_576) return `${Math.ceil(chars / 1024)} KB`;
  return `${(chars / 1_048_576).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Formats a value for the viewer: strings verbatim, anything else as indented JSON with
 * sorted keys. Never throws, so a cyclic or BigInt value still renders.
 */
export function formatResponseText(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return toSortedJson(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Builds the tabs of the response viewer for an entry point, keeping only the ones that
 * have content.
 *
 * @param data Response to show.
 * @param mode Entry point; Process Records shows Response and Request only.
 */
export function buildResponseTabs(
  data: ServerResponseData,
  mode: ServerResponseMode,
): ServerResponseTab[] {
  if (mode === 'process-record') {
    return [
      { id: 'response', label: 'Response' },
      { id: 'request', label: 'Request' },
    ];
  }
  const tabs: ServerResponseTab[] = [];
  const kind = classifyServerResponse(data.body).kind;
  if (kind === 'envelope' || kind === 'error' || kind === 'message') {
    tabs.push({ id: 'summary', label: translateNow('common.serverResponse.summary') });
  }
  if (kind !== 'empty') tabs.push({ id: 'json', label: 'JSON' });
  if (!isEmptyValue(data.requestBody)) tabs.push({ id: 'request', label: 'Request' });
  if (!isEmptyValue(data.headers)) tabs.push({ id: 'headers', label: 'Headers' });
  return tabs;
}

/**
 * Maps a notification to what the response viewer shows. A notification without an HTTP status
 * (a client-side message) carries only its title and message.
 */
export function notificationToResponse(notif: ServerNotification): ServerResponseData {
  return {
    status: notif.statusCode,
    method: notif.method,
    url: notif.url,
    responseTimeMs: notif.responseTimeMs,
    timestamp: notif.timestamp,
    title: notif.title,
    message: notif.message,
    body: notif.payload,
    requestBody: notif.requestPayload,
    headers: notif.headers,
  };
}
